import { Suspense } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'react-hot-toast';

// Initialize i18n
import './i18n/config';

// Authentication
import { AuthProvider } from './contexts/AuthContext';
import { AuthGuard } from './components/AuthGuard';

// Layouts (keep eagerly loaded as it's used everywhere)
import { MainLayout } from './shared/layouts/MainLayout';

// Eagerly load the main page
import { DataInputPage } from './features/data-input/pages/DataInputPage';

// Import OptimizationAnalysisPage directly to debug the issue
import { OptimizationAnalysisPage } from './features/optimization/pages/OptimizationAnalysisPage';

// Error boundary for chunk loading errors
import { ChunkLoadError } from './components/error/ChunkLoadError';

// Import lazy with retry logic
import { lazyRetry } from './utils/lazyRetry';

// Lazy load optimization pages with automatic retry on chunk load failure
const ConfigurationPage = lazyRetry(() => import('./features/optimization/pages/ConfigurationPageRefactored').then(m => ({ default: m.ConfigurationPage })));
const OptimizationRunnerPage = lazyRetry(() => import('./features/optimization/pages/OptimizationRunnerPage').then(m => ({ default: m.OptimizationRunnerPage })));
const OptimizationResultsPage = lazyRetry(() => import('./features/optimization/pages/OptimizationResultsPage').then(m => ({ default: m.OptimizationResultsPage })));
const FinancialConfigurationPage = lazyRetry(() => import('./features/financial/pages/FinancialConfigurationPage').then(m => ({ default: m.FinancialConfigurationPage })));
const FinancialAnalysisPage = lazyRetry(() => import('./features/financial/pages/FinancialAnalysisPage').then(m => ({ default: m.FinancialAnalysisPage })));
const CommercialPresentationPage = lazyRetry(() => import('./features/financial/pages/CommercialPresentationPage').then(m => ({ default: m.CommercialPresentationPage })));
const ResultsPage = lazyRetry(() => import('./features/results/pages/ResultsPage').then(m => ({ default: m.ResultsPage })));
const PortfolioPage = lazyRetry(() => import('./features/portfolio/pages/PortfolioPage').then(m => ({ default: m.PortfolioPage })));

// Loading component for lazy loaded pages
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Define routes
const router = createBrowserRouter([
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <ChunkLoadError />,
    children: [
      {
        index: true,
        element: <DataInputPage />,
      },
      {
        path: 'optimization',
        errorElement: <ChunkLoadError />,
        children: [
          {
            index: true,
            element: <Suspense fallback={<PageLoader />}><ConfigurationPage /></Suspense>,
            errorElement: <ChunkLoadError />,
          },
          {
            path: 'configure',
            element: <Suspense fallback={<PageLoader />}><ConfigurationPage /></Suspense>,
            errorElement: <ChunkLoadError />,
          },
          {
            path: 'analysis',
            element: <OptimizationAnalysisPage />,
            errorElement: <ChunkLoadError />,
          },
          {
            path: 'run',
            element: <Suspense fallback={<PageLoader />}><OptimizationRunnerPage /></Suspense>,
            errorElement: <ChunkLoadError />,
          },
          {
            path: 'results',
            element: <Suspense fallback={<PageLoader />}><OptimizationResultsPage /></Suspense>,
            errorElement: <ChunkLoadError />,
          },
        ],
      },
      {
        path: 'results',
        errorElement: <ChunkLoadError />,
        children: [
          {
            index: true,
            element: <Suspense fallback={<PageLoader />}><ResultsPage /></Suspense>,
            errorElement: <ChunkLoadError />,
          },
          {
            path: 'financial',
            errorElement: <ChunkLoadError />,
            children: [
              {
                index: true,
                element: <Suspense fallback={<PageLoader />}><FinancialConfigurationPage /></Suspense>,
                errorElement: <ChunkLoadError />,
              },
              {
                path: 'configure',
                element: <Suspense fallback={<PageLoader />}><FinancialConfigurationPage /></Suspense>,
                errorElement: <ChunkLoadError />,
              },
              {
                path: 'results',
                element: <Suspense fallback={<PageLoader />}><FinancialAnalysisPage /></Suspense>,
                errorElement: <ChunkLoadError />,
              },
              {
                path: 'commercial',
                element: <Suspense fallback={<PageLoader />}><CommercialPresentationPage /></Suspense>,
                errorElement: <ChunkLoadError />,
              },
            ],
          },
        ],
      },
      {
        path: 'portfolio',
        element: <Suspense fallback={<PageLoader />}><PortfolioPage /></Suspense>,
        errorElement: <ChunkLoadError />,
      },
    ],
  },
]);

function App() {
  // Check if authentication is enabled via environment variable
  const isAuthEnabled = import.meta.env.VITE_ENABLE_AUTH === 'true';

  // Main app content
  const appContent = (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#1f2937',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#37ade3',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#dc2626',
              secondary: '#fff',
            },
          },
        }}
      />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );

  // Conditionally wrap with authentication based on environment variable
  if (isAuthEnabled) {
    return (
      <AuthProvider>
        <AuthGuard>
          {appContent}
        </AuthGuard>
      </AuthProvider>
    );
  }

  // Return app without authentication when disabled
  return appContent;
}

export default App
